import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, BookOpen, Search, ChevronDown, ChevronUp, Save, TrendingUp, DollarSign, AlertCircle, Percent, Utensils } from 'lucide-react';
import { useRecipes, useItems, useUpsertRecipe } from '../hooks/useInventory';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../utils/api';
import { calculateIngredientCost, normalizeUnit } from '../utils/unitConverter';
import { SubRecipesTab } from '../components/SubRecipesTab';

// ── Types ──────────────────────────────────────────────────────────────────────
interface IngredientRow {
  itemId: string;
  qty: number;
  unit: string;
  wastePct?: number;
  notes: string;
}

interface PosProduct {
  id: string | number;
  name: string;
  price: number;
  category?: { name?: string } | string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const COMMON_UNITS = [
  'g', 'kg', 'lb', 'oz',
  'ml', 'litros', 'tazas', 'cucharadas', 'cucharaditas',
  'unidades', 'porciones', 'cajas', 'paquetes'
];

function IngredientRowForm({
  row, index, items, onChange, onRemove,
}: {
  row: IngredientRow;
  index: number;
  items: { id: string; name: string; unit: string; cost?: number; conversion_factor?: number }[];
  onChange: (index: number, field: keyof IngredientRow, value: string | number) => void;
  onRemove: (index: number) => void;
}) {
  const selectedItem = items.find((i) => String(i.id) === String(row.itemId));
  
  const calc = useMemo(() => {
    if (!selectedItem) return { netQtyInItemUnit: 0, grossQtyInItemUnit: 0, cost: 0 };
    return calculateIngredientCost(
      row.qty,
      row.unit,
      selectedItem.cost ?? 0,
      selectedItem.unit ?? 'unidades',
      selectedItem.conversion_factor ?? 1,
      row.wastePct ?? 0
    );
  }, [row.qty, row.unit, row.wastePct, selectedItem]);

  const hasUnitConversion = selectedItem && normalizeUnit(row.unit) !== normalizeUnit(selectedItem.unit);

  return (
    <div className="grid grid-cols-12 gap-2 items-center py-2.5 border-b border-divider/40 last:border-0 hover:bg-surface-muted/30 px-2 rounded-xl transition">
      {/* Selector de Insumo */}
      <div className="col-span-12 sm:col-span-4">
        <label className="sm:hidden text-[10px] uppercase font-bold text-muted mb-0.5 block">Insumo</label>
        <select
          value={row.itemId}
          onChange={(e) => {
            const newItemId = e.target.value;
            onChange(index, 'itemId', newItemId);
            const found = items.find((i) => String(i.id) === String(newItemId));
            if (found?.unit) {
              onChange(index, 'unit', found.unit);
            }
          }}
          className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium"
          aria-label="Ingrediente"
        >
          <option value="">-- Seleccionar insumo --</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>{i.name} ({i.unit}) — ${Number(i.cost ?? 0).toLocaleString()}/{i.unit}</option>
          ))}
        </select>
      </div>

      {/* Cantidad */}
      <div className="col-span-4 sm:col-span-2">
        <label className="sm:hidden text-[10px] uppercase font-bold text-muted mb-0.5 block">Cantidad</label>
        <input
          type="number"
          min={0}
          step="any"
          value={row.qty === 0 ? '' : row.qty}
          onChange={(e) => onChange(index, 'qty', parseFloat(e.target.value) || 0)}
          className="w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-center font-bold"
          placeholder="0"
          aria-label="Cantidad"
        />
      </div>

      {/* Unidad */}
      <div className="col-span-4 sm:col-span-2">
        <label className="sm:hidden text-[10px] uppercase font-bold text-muted mb-0.5 block">Unidad</label>
        <select
          value={row.unit}
          onChange={(e) => onChange(index, 'unit', e.target.value)}
          className="w-full rounded-xl border border-divider bg-surface px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium"
          aria-label="Unidad"
        >
          {COMMON_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>

      {/* % Merma / Desperdicio */}
      <div className="col-span-4 sm:col-span-1">
        <label className="sm:hidden text-[10px] uppercase font-bold text-muted mb-0.5 block">% Merma</label>
        <div className="relative">
          <input
            type="number"
            min={0}
            max={90}
            step="1"
            value={row.wastePct || ''}
            onChange={(e) => onChange(index, 'wastePct', parseFloat(e.target.value) || 0)}
            placeholder="0%"
            title="Porcentaje de merma o desperdicio al preparar (ej: 10% cáscara/cocción)"
            className="w-full rounded-xl border border-divider bg-surface px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary text-center"
            aria-label="% Merma"
          />
        </div>
      </div>

      {/* Costo Calculado con Conversión */}
      <div className="col-span-8 sm:col-span-2 text-right pr-2">
        <span className="text-[10px] block text-muted leading-tight">Costo real</span>
        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
          ${calc.cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </span>
        {hasUnitConversion && (
          <span className="text-[9px] block text-muted leading-none font-mono">
            {calc.netQtyInItemUnit} {selectedItem?.unit}
          </span>
        )}
      </div>

      {/* Botón Eliminar */}
      <div className="col-span-4 sm:col-span-1 flex justify-center">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex h-9 w-9 items-center justify-center rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition"
          aria-label="Eliminar ingrediente"
          title="Eliminar ingrediente"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const RecipesPage: React.FC = () => {
  const [mainTab, setMainTab] = useState<'DISHES' | 'SUBRECIPES'>('DISHES');
  const [recipeSearch, setRecipeSearch] = useState('');
  const { data: recipes, isLoading: loadingRecipes } = useRecipes(recipeSearch);
  const { data: itemsPage } = useItems({ limit: 500 });
  const upsert = useUpsertRecipe();

  // Load POS products from the main API
  const { data: posProducts } = useQuery<PosProduct[]>({
    queryKey: ['products', 'forRecipes'],
    queryFn: async () => {
      const res = await api.get('/products');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    staleTime: 60_000,
  });

  const inventoryItems = (itemsPage?.data ?? []).map((i) => ({
    id: String(i.id),
    name: i.name,
    unit: i.unit ?? 'unidades',
    cost: Number(i.cost ?? 0),
    conversion_factor: Number(i.conversion_factor ?? 1),
  }));

  // ── Local state ────────────────────────────────────────────────────────────
  const [selectedProductId, setSelectedProductId] = useState('');
  const [recipeName, setRecipeName] = useState('');
  const [ingredients, setIngredients] = useState<IngredientRow[]>([
    { itemId: '', qty: 1, unit: 'g', wastePct: 0, notes: '' },
  ]);
  const [q, setQ] = useState('');
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);

  const selectedProduct = (posProducts ?? []).find((p) => String(p.id) === String(selectedProductId));
  const productPrice = Number(selectedProduct?.price ?? 0);

  // Costo total de la receta activa en edición
  const currentRecipeCost = useMemo(() => {
    let total = 0;
    ingredients.forEach((row) => {
      if (!row.itemId) return;
      const item = inventoryItems.find((i) => String(i.id) === String(row.itemId));
      if (item) {
        const calc = calculateIngredientCost(
          row.qty,
          row.unit,
          item.cost,
          item.unit,
          item.conversion_factor,
          row.wastePct ?? 0
        );
        total += calc.cost;
      }
    });
    return total;
  }, [ingredients, inventoryItems]);

  const foodCostPct = productPrice > 0 ? (currentRecipeCost / productPrice) * 100 : 0;
  const marginNet = productPrice - currentRecipeCost;
  const suggestedPrice30 = currentRecipeCost > 0 ? Math.round(currentRecipeCost / 0.30 / 100) * 100 : 0;
  const suggestedPrice35 = currentRecipeCost > 0 ? Math.round(currentRecipeCost / 0.35 / 100) * 100 : 0;

  const filteredRecipes = (recipes ?? []).filter((r) =>
    r.name?.toLowerCase().includes(q.toLowerCase()) ||
    String(r.productId).toLowerCase().includes(q.toLowerCase())
  );

  const handleIngredientChange = (index: number, field: keyof IngredientRow, value: string | number) => {
    setIngredients((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addIngredient = () => {
    setIngredients((prev) => [...prev, { itemId: '', qty: 1, unit: 'g', wastePct: 0, notes: '' }]);
  };

  const removeIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!selectedProductId) { toast.error('Selecciona un producto del POS'); return; }
    const valid = ingredients.filter((i) => i.itemId && i.qty > 0);
    if (valid.length === 0) { toast.error('Agrega al menos un ingrediente válido'); return; }

    upsert.mutate(
      {
        productId: String(selectedProductId),
        name: recipeName || undefined,
        ingredients: valid.map((i) => ({
          itemId: i.itemId,
          qty: i.qty,
          unit: i.unit,
          wastePct: i.wastePct ?? 0,
          notes: i.notes || undefined,
        })),
      },
      {
        onSuccess: () => {
          toast.success('¡Receta y escandallo guardados correctamente!');
          setSelectedProductId('');
          setRecipeName('');
          setIngredients([{ itemId: '', qty: 1, unit: 'g', wastePct: 0, notes: '' }]);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message ?? 'Error al guardar la receta');
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* Header con tabs segmentados */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Control Gastronómico</p>
            <h1 className="text-3xl font-extrabold text-[color:var(--color-text)] flex items-center gap-3">
              <BookOpen size={28} className="text-primary" /> Fichas Técnicas y Recetas
            </h1>
            <p className="mt-1 text-sm text-muted">
              Escandallos con conversión inteligente de unidades, Food Cost % automático y costeo exacto.
            </p>
          </div>

          <div className="inline-flex rounded-2xl border border-divider bg-surface-muted/50 p-1 shrink-0">
            <button
              onClick={() => setMainTab('DISHES')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
                mainTab === 'DISHES'
                  ? 'bg-surface text-primary shadow-xs'
                  : 'text-muted hover:text-[color:var(--color-text)]'
              }`}
            >
              <BookOpen size={15} /> Platos & Cócteles
            </button>
            <button
              onClick={() => setMainTab('SUBRECIPES')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
                mainTab === 'SUBRECIPES'
                  ? 'bg-surface text-primary shadow-xs'
                  : 'text-muted hover:text-[color:var(--color-text)]'
              }`}
            >
              <Utensils size={15} /> Sub-recetas (Mise en Place)
            </button>
          </div>
        </div>

        {mainTab === 'SUBRECIPES' ? (
          <SubRecipesTab />
        ) : (
          <>
            {/* Formulario Principal de Receta */}
        <div className="rounded-3xl border border-divider bg-surface p-6 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-divider pb-4">
            <h2 className="text-lg font-bold text-[color:var(--color-text)]">
              {selectedProductId ? 'Editar Ficha Técnica' : 'Nueva Receta de Producto'}
            </h2>
            {selectedProduct && (
              <span className="text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary">
                Precio POS: ${productPrice.toLocaleString()}
              </span>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="product-select">
                Producto del Catálogo POS *
              </label>
              <select
                id="product-select"
                value={selectedProductId}
                onChange={(e) => {
                  const prodId = e.target.value;
                  setSelectedProductId(prodId);
                  // Buscar si ya tiene receta existente para pre-cargarla
                  const existing = (recipes ?? []).find((r) => String(r.productId) === String(prodId));
                  if (existing) {
                    setRecipeName(existing.name ?? '');
                    setIngredients(existing.ingredients.map((ing) => ({
                      itemId: ing.item.id,
                      qty: ing.qty,
                      unit: ing.unit,
                      wastePct: ing.wastePct ?? 0,
                      notes: ing.notes ?? '',
                    })));
                  }
                }}
                className="w-full rounded-2xl border border-divider bg-surface-muted px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-medium"
              >
                <option value="">-- Selecciona el plato o bebida --</option>
                {(posProducts ?? []).map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} (${Number(p.price).toLocaleString()}) {typeof p.category === 'object' ? `(${p.category?.name})` : p.category ? `(${p.category})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="recipe-name">
                Nombre de la preparación (opcional)
              </label>
              <input
                id="recipe-name"
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder="Ej: Hamburguesa Clásica 200g"
                className="w-full rounded-2xl border border-divider bg-surface-muted px-3.5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Tarjetas de Rentabilidad y Food Cost (Semáforo) */}
          {selectedProductId && (
            <div className="rounded-2xl border border-divider p-5 bg-surface-muted/60 space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="rounded-xl bg-surface p-3.5 border border-divider shadow-xs">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1">
                    <DollarSign size={12} /> Costo Ingredientes
                  </p>
                  <p className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400">
                    ${currentRecipeCost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </p>
                  <span className="text-[10px] text-muted">Costo neto por plato</span>
                </div>

                <div className="rounded-xl bg-surface p-3.5 border border-divider shadow-xs">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1">
                    <Percent size={12} /> Food Cost %
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-extrabold text-[color:var(--color-text)]">
                      {foodCostPct.toFixed(1)}%
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      foodCostPct <= 30
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                        : foodCostPct <= 40
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                    }`}>
                      {foodCostPct <= 30 ? 'Óptimo' : foodCostPct <= 40 ? 'Moderado' : 'Crítico'}
                    </span>
                  </div>
                  <span className="text-[10px] text-muted">Meta ideal: 25% – 35%</span>
                </div>

                <div className="rounded-xl bg-surface p-3.5 border border-divider shadow-xs">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1">
                    <TrendingUp size={12} /> Margen Bruto ($)
                  </p>
                  <p className={`text-xl font-extrabold ${marginNet >= 0 ? 'text-primary' : 'text-red-500'}`}>
                    ${marginNet.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                  </p>
                  <span className="text-[10px] text-muted">Ganancia bruta por venta</span>
                </div>

                <div className="rounded-xl bg-surface p-3.5 border border-divider shadow-xs">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1 flex items-center gap-1">
                    <AlertCircle size={12} /> Precio Sugerido
                  </p>
                  <p className="text-base font-extrabold text-[color:var(--color-text)]">
                    ${suggestedPrice30.toLocaleString()}
                  </p>
                  <span className="text-[10px] text-muted">Para Food Cost del 30%</span>
                </div>
              </div>
            </div>
          )}

          {/* Lista de Ingredientes de la Receta */}
          <div className="space-y-3">
            <div className="hidden sm:grid grid-cols-12 gap-2 px-2 text-[10px] font-bold uppercase text-muted">
              <div className="col-span-4">Insumo del Inventario</div>
              <div className="col-span-2 text-center">Cantidad</div>
              <div className="col-span-2">Unidad Receta</div>
              <div className="col-span-1 text-center">% Merma</div>
              <div className="col-span-2 text-right pr-2">Costo Real</div>
              <div className="col-span-1 text-center">Acción</div>
            </div>

            <div className="divide-y divide-divider/30">
              {ingredients.map((row, i) => (
                <IngredientRowForm
                  key={i}
                  row={row}
                  index={i}
                  items={inventoryItems}
                  onChange={handleIngredientChange}
                  onRemove={removeIngredient}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-divider">
            <button
              type="button"
              onClick={addIngredient}
              className="inline-flex items-center gap-2 rounded-2xl border border-divider px-4 py-2.5 text-sm font-semibold transition hover:bg-surface-muted cursor-pointer"
            >
              <Plus size={16} className="text-primary" /> Agregar ingrediente
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-2 rounded-2xl bg-primary px-7 py-3 text-sm font-bold text-white shadow-md transition hover:opacity-90 disabled:opacity-60 cursor-pointer"
            >
              <Save size={16} />
              {upsert.isPending ? 'Guardando ficha...' : 'Guardar Ficha Técnica'}
            </button>
          </div>
        </div>

        {/* Catálogo de Recetas Existentes */}
        <div className="rounded-3xl border border-divider bg-surface p-6 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-[color:var(--color-text)]">Recetas Registradas ({filteredRecipes.length})</h2>
              <p className="text-xs text-muted">Fichas técnicas con costeo automatizado por venta</p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Buscar receta o plato..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-2xl border border-divider bg-surface-muted pl-9 pr-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {loadingRecipes ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-surface-muted" />
            ))}</div>
          ) : filteredRecipes.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted">
              <BookOpen size={40} className="mx-auto mb-2 text-muted/40" />
              No hay recetas registradas todavía. Selecciona un plato arriba para crear la primera.
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRecipes.map((recipe) => {
                const isOpen = expandedRecipe === recipe._id;
                const product = (posProducts ?? []).find((p) => String(p.id) === String(recipe.productId));
                const price = Number(recipe.productPrice ?? product?.price ?? 0);
                const cost = Number(recipe.totalCost ?? 0);
                const foodCost = price > 0 ? (cost / price) * 100 : 0;

                return (
                  <div key={recipe._id} className="rounded-2xl border border-divider overflow-hidden bg-surface transition">
                    <div
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 cursor-pointer hover:bg-surface-muted/40 gap-3"
                      onClick={() => setExpandedRecipe(isOpen ? null : recipe._id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-[color:var(--color-text)]">
                            {recipe.name ?? product?.name ?? `Producto #${recipe.productId}`}
                          </p>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            foodCost <= 30
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                              : foodCost <= 40
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                          }`}>
                            FC: {foodCost.toFixed(1)}%
                          </span>
                        </div>
                        <p className="text-xs text-muted mt-0.5">
                          {recipe.ingredients.length} insumo(s) · Costo: <strong className="text-emerald-600 dark:text-emerald-400">${cost.toLocaleString()}</strong> · Venta: <strong>${price.toLocaleString()}</strong>
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-xs text-primary font-semibold hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProductId(recipe.productId);
                            setRecipeName(recipe.name ?? '');
                            setIngredients(recipe.ingredients.map((ing) => ({
                              itemId: ing.item.id,
                              qty: ing.qty,
                              unit: ing.unit,
                              wastePct: ing.wastePct ?? 0,
                              notes: ing.notes ?? '',
                            })));
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Editar
                        </button>
                        {isOpen ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
                      </div>
                    </div>

                    {isOpen && (
                      <div className="border-t border-divider bg-surface-muted/50 p-4">
                        <div className="space-y-2">
                          {recipe.ingredients.map((ing, i) => (
                            <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-divider/30 last:border-0">
                              <span className="font-semibold text-[color:var(--color-text)]">
                                {ing.item.name}
                              </span>
                              <span className="text-muted">
                                {ing.qty} {ing.unit} {ing.wastePct ? `(+${ing.wastePct}% merma)` : ''} · Subtotal: <strong className="text-emerald-600">${Number(ing.ingredientCost ?? (ing.qty * (ing.item.cost ?? 0))).toLocaleString()}</strong>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
          </>
        )}

      </div>
    </div>
  );
};

export default RecipesPage;
